import { type FC, useEffect } from 'react'
import { Form } from 'react-bootstrap'
import { NavLink } from 'react-router-dom'
import * as Sentry from '@sentry/react'
import { Button } from '../../../../components/Button/Button'
import { Tooltip } from '../../../../components/Tooltip/Tooltip'
import { UserAvatar } from '../../../../components/UserAvatar/UserAvatar'
import { useUserAvatarProps } from '../../../../components/UserAvatar/useUserAvatarProps'
import { type AccountSchedulingSettings, type UserSchedulingSettings } from '../../../../models/Scheduling'
import { useAppDispatch, useAppSelector } from '../../../../store/hooks'
import { selectSafeAuthenticatedUser } from '../../../../store/auth/authSlice'
import { addToast } from '../../../../store/toast/toastSlice'
import { SettingsCard, SettingsRow, SettingsSection } from '../../../Setup/Scheduling/sections/SettingsSection'
import { PreviewLabel } from '../../../Scheduling/shared/PreviewLabel'
import { BOOKING_BASE_URL } from '../../../Scheduling/shared/bookingBaseUrl'
import { type UpdateUserSettings } from '../SchedulingProfileTab'

interface SectionProps {
  userSettings: UserSchedulingSettings
  accountSettings?: AccountSchedulingSettings
  isSaving?: boolean
  onUpdate: UpdateUserSettings
}

export const YourBookingPageSection: FC<SectionProps> = ({ userSettings, accountSettings, isSaving = false, onUpdate }) => {
  const dispatch = useAppDispatch()
  const authenticatedUser = useAppSelector(selectSafeAuthenticatedUser)
  const avatarProps = useUserAvatarProps({ userId: authenticatedUser.id, width: '48px', height: '48px', fontSize: '24px' })
  const bookingPage = userSettings.bookingPage
  const accountSlug = accountSettings?.publicBookingPage?.accountSlug
  const userSlug = bookingPage?.slug
  const bookingUrl = accountSlug && userSlug ? `${BOOKING_BASE_URL}/${accountSlug}/${userSlug}` : null

  useEffect(() => {
    if (accountSettings && !accountSlug) {
      Sentry.captureException(new Error('Scheduling: account is missing accountSlug; cannot build a booking URL in your booking page section'))
    }
  }, [accountSettings, accountSlug])

  const handleCopy = async () => {
    if (!bookingUrl) {
      return
    }
    try {
      await navigator.clipboard.writeText(`https://${bookingUrl}`)
      dispatch(addToast({ variant: 'success', title: 'Copied', messages: ['Your booking link is on the clipboard.'] }))
    } catch (error) {
      Sentry.captureException(error)
      dispatch(addToast({ variant: 'error', title: 'Could not copy', messages: ['Copy the booking link manually instead.'] }))
    }
  }

  return (
    <SettingsSection title='Your public booking page' subtitle='Where clients book meetings with you.' isSaving={isSaving}>
      <SettingsCard>
        <SettingsRow className='d-flex align-items-start justify-content-between gap-3'>
          <div className='d-flex flex-column'>
            <span className='bold'>Public booking links</span>
            <span className='paragraph-small zocks-text-gray-600'>
              Allow clients to book through your public booking page. When disabled, your clients can only book through the links you send them.
            </span>
          </div>
          <Form.Switch
            aria-label='Public booking links'
            checked={userSettings.bookingRules?.publicBookingLinksEnabled ?? false}
            onChange={(event) => onUpdate({ bookingRules: { ...userSettings.bookingRules, publicBookingLinksEnabled: event.currentTarget.checked } })}
          />
        </SettingsRow>
        <SettingsRow className='d-flex flex-wrap align-items-center justify-content-between gap-3'>
          <div className='d-flex flex-column'>
            <span className='bold'>Your booking URL</span>
            <span className='paragraph-small zocks-text-gray-600'>Share this link so clients can book time with you.</span>
          </div>
          {bookingUrl ? (
            <div className='d-flex align-items-center zocks-gap-0.5'>
              <span className='paragraph-small zocks-text-gray-600'>{bookingUrl}</span>
              <Button variant='borderlessGrayButton' className='p-1' aria-label='Copy booking link' onClick={() => void handleCopy()}>
                <i className='bi bi-copy' />
              </Button>
              <a href={`https://${bookingUrl}`} target='_blank' rel='noreferrer' aria-label='Open booking page' className='zocks-text-gray-600'>
                <i className='bi bi-box-arrow-up-right' />
              </a>
              <Tooltip title='To customize booking URLs, ask your account owner to reach out to their implementation manager at Zocks.'>
                <i className='bi bi-info-circle zocks-text-gray-500' />
              </Tooltip>
            </div>
          ) : (
            <span className='paragraph-small zocks-text-danger'>Booking URL unavailable</span>
          )}
        </SettingsRow>
        <SettingsRow className='d-flex align-items-center justify-content-between gap-3'>
          <div className='d-flex flex-column'>
            <span className='bold'>Show your profile photo</span>
            <span className='paragraph-small zocks-text-gray-600'>
              Display your profile photo on the booking page. Upload in <NavLink to='/account/profile-settings'>Profile settings</NavLink>.
            </span>
          </div>
          <div className='d-flex align-items-center gap-3'>
            <PreviewLabel stacked shownToClients={bookingPage?.showProfilePhoto ?? false} />
            {bookingPage?.showProfilePhoto ? (
              <UserAvatar className='flex-shrink-0' {...avatarProps} />
            ) : (
              <UserAvatar className='flex-shrink-0' icon='' width={avatarProps.width} height={avatarProps.height} />
            )}
            <Form.Switch
              aria-label='Show your profile photo'
              checked={bookingPage?.showProfilePhoto ?? false}
              onChange={(event) => onUpdate({ bookingPage: { ...bookingPage, showProfilePhoto: event.currentTarget.checked } })}
            />
          </div>
        </SettingsRow>
      </SettingsCard>
    </SettingsSection>
  )
}
